define((require) => {
    "use strict";

    const Action       = require("shared/ui/experience/action/Action");
    const Window       = require("shared/services/Window");
    const ViewerEvents = require("shared/services/ViewerEvents");

    const TYPE = "link";

    const CURRENT_WINDOW = "_top";   // "_top", not "_parent", to ensure embedded Experiences/Groups also works
    const NEW_WINDOW     = "_blank";

    /**
     * Action that opens a link.
     *
     * @class LinkAction
     * @constructor
     */
    return Action.extend("LinkAction", {

        defaults: {
            url:    "",
            target: CURRENT_WINDOW // defaults to CURRENT_WINDOW (_top) because of legacy Editor behaviour doing the same!
        },

        _event: ViewerEvents.LINK_VISIT,

        /**
         * @protected
         * Sub-type hook to return the full url to open via this link option, defaulting to just returning the
         * "url" attribute. <p>
         *
         * The url does currently not support linking to a normalised Experience or Group, but can be an absolute
         * "../viewer.html#.." link just fine.
         *
         * @return {String} The url to visit; can be falsey (which means this action cannot be activated).
         */
        _url() {
            return this.get("url");
        },

        _activateable(experienceViewModel) {
            return this._url(experienceViewModel) ? null : "Missing url";
        },

        /**
         * Create an anchor tag with the necessary attributes.
         *
         * @param {HTMLElement} rootElement - The parent html element
         * @param {title} string - The parent models title
         * @returns {ActionDomElement}      - Returns an anchor tag if none is present. If one is present returns an empty object.
         */
        getElement(rootElement, title) {
            // We have to check if an anchor tag is already present, since the updateRendering in the AbstractWidgetView is called twice.
            // In the case of the TextWidget CKEditor will strip out the <a> tag.
            // The other widgets (Image/Link) where the LinkAction can be applied, still have the Anchor since it hasn't been removed.
            if (!rootElement.querySelector("a")) {
                const anchorTag = document.createElement("a");

                anchorTag.setAttribute("href",   this.get("url"));
                anchorTag.setAttribute("target", this.get("target") || "_top");
                anchorTag.addEventListener("click", (e) => {
                    e.preventDefault();
                    return false;
                });

                if (!rootElement.firstChild) {
                    const url = this.get("url");
                    const elementTitle = title && title.length > 0 ? title : url;
                    anchorTag.setAttribute("title", elementTitle);
                }

                return {
                    element:    anchorTag,
                    isWrapper:  true
                };
            }

            return {
                element:    null,
                isWrapper:  false
            };
        },

        _activate(experienceViewModel) {
            const url    = this._url(experienceViewModel);
            const target = this.get("target") || CURRENT_WINDOW;
            const win    = Window.topWindow();

            // Resolve relative URLs to the top level window, if available. If not, the top most window will be
            // the outermost Experience accessible via a direct url. Strictly speaking we are in client
            // space here, but we do not set "clientSpace" to true since we might be able to handle
            // the link anyways, i.e. can potentially work outside client space...
            const a = win.document.createElement("a");
            a.href = url;
            Window.open(a.href, target); // will make the ResponsiveViewerController pause this Experience if the new target tab/window is shown

            const eventArgs = {context: TYPE, url: a.href, target}; // include "target" as it may have defaulted
            if (url !== a.href) {
                eventArgs.relative = url;
            }
            return Object.assign({actionId: this.id, widgetId: this.attributes.widgetId}, eventArgs);
        }

    }, {
        TYPE,
        CURRENT_WINDOW,
        NEW_WINDOW
    });
});



// WEBPACK FOOTER //
// ./src/shared/ui/experience/action/link/LinkAction.js